import {
  type ColumnDef,
  createColumnHelper,
  DataTable,
  type PaginationState,
} from "@aurumiq/ui/composite/data-table";
import { StatusTag, statusVariant } from "@aurumiq/ui/composite/status-tag";
import { dmy, dmyTime, relativeTime } from "@aurumiq/ui/lib/format";
import { useState } from "react";
import { Link } from "react-router";
import { NativeSelect } from "@/components/field";
import { SUB_STATUSES } from "@/features/orgs/subscription-dialog";
import { errorMessage } from "@/lib/api";
import type { AdminSubscription } from "@/lib/types";
import { useSubscriptions } from "./queries";

const col = createColumnHelper<AdminSubscription>();

const columns = [
  col.accessor("org_name", {
    header: "Organisation",
    cell: (c) => (
      <Link
        to={`/orgs/${c.row.original.org_id}/billing`}
        className="font-semibold text-primary hover:underline"
      >
        {c.getValue()}
        <span className="ml-1.5 font-mono font-normal text-muted-foreground text-xs">
          {c.row.original.org_code}
        </span>
      </Link>
    ),
  }),
  col.accessor("plan_code", { header: "Plan" }),
  col.accessor("status", {
    header: "Status",
    cell: (c) => (
      <StatusTag variant={statusVariant(c.getValue())}>{c.getValue().toUpperCase()}</StatusTag>
    ),
  }),
  col.accessor("seats", { header: "Seats", meta: { numeric: true } }),
  col.accessor("interval", { header: "Interval", cell: (c) => c.getValue() ?? "—" }),
  col.accessor("current_period_end", {
    header: "Period end",
    cell: (c) => (c.getValue() ? dmy(c.getValue()) : "—"),
  }),
  col.accessor("sub_until", {
    header: "Sub until",
    cell: (c) => (c.getValue() ? dmy(c.getValue()) : "—"),
  }),
  col.accessor("stripe_subscription_id", {
    header: "Stripe",
    cell: (c) =>
      c.getValue() ? (
        <a
          href={`https://dashboard.stripe.com/subscriptions/${c.getValue()}`}
          target="_blank"
          rel="noreferrer"
          className="font-mono text-primary text-xs hover:underline"
        >
          {c.getValue()}
        </a>
      ) : (
        "—"
      ),
  }),
  col.accessor("updated_at", {
    header: "Updated",
    cell: (c) => <span title={dmyTime(c.getValue())}>{relativeTime(c.getValue())}</span>,
  }),
] as ColumnDef<AdminSubscription, unknown>[];

export function SubscriptionsTab() {
  const [status, setStatus] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });
  const subs = useSubscriptions(status, pagination.pageIndex + 1);
  const total = subs.data?.total ?? 0;
  return (
    <DataTable
      columns={columns}
      data={subs.data?.items ?? []}
      isLoading={subs.isPending}
      getRowId={(s) => s.id}
      pageCount={Math.max(1, Math.ceil(total / pagination.pageSize))}
      pagination={pagination}
      onPaginationChange={setPagination}
      emptyMessage={
        subs.isError
          ? `Could not load subscriptions: ${errorMessage(subs.error)}`
          : "No subscriptions."
      }
      toolbar={
        <NativeSelect
          aria-label="Status filter"
          className="w-44"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPagination((p) => ({ ...p, pageIndex: 0 }));
          }}
        >
          <option value="">All statuses</option>
          {SUB_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </NativeSelect>
      }
    />
  );
}
